import React, { useEffect, useState } from "react";
import {
  Box,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  MEDIA_KIT_METRICS_WINDOW_DEFAULTS,
  MEDIA_KIT_METRICS_WINDOW_MAX_DAYS,
  type MediaKitMetricsWindow,
} from "../api/podcasts";

export function normalizeMetricsWindow(
  raw: Partial<MediaKitMetricsWindow> | null | undefined,
): MediaKitMetricsWindow {
  const d = MEDIA_KIT_METRICS_WINDOW_DEFAULTS;
  const clamp = (v: unknown, fallback: number) => {
    const n = Math.round(Number(v));
    if (!Number.isFinite(n)) return fallback;
    return Math.min(MEDIA_KIT_METRICS_WINDOW_MAX_DAYS, Math.max(1, n));
  };
  return {
    enabled: raw?.enabled === true,
    longFormDays: clamp(raw?.longFormDays, d.longFormDays),
    shortsDays: clamp(raw?.shortsDays, d.shortsDays),
  };
}

export function formatMetricsWindowSummary(w: MediaKitMetricsWindow): string {
  return w.enabled
    ? `Episodios día ${w.longFormDays} · Shorts día ${w.shortsDays}`
    : "Métricas acumuladas";
}

type DayFieldProps = {
  label: string;
  value: number;
  disabled?: boolean;
  onCommit: (days: number) => void;
};

/** Commits on blur / Enter so typing "1" on the way to "14" doesn't save twice. */
const DayField: React.FC<DayFieldProps> = ({
  label,
  value,
  disabled,
  onCommit,
}) => {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);

  const n = Math.round(Number(draft));
  const invalid =
    !Number.isFinite(n) || n < 1 || n > MEDIA_KIT_METRICS_WINDOW_MAX_DAYS;

  const commit = () => {
    if (invalid) {
      setDraft(String(value));
      return;
    }
    if (n !== value) onCommit(n);
  };

  return (
    <TextField
      label={label}
      type="number"
      size="small"
      value={draft}
      disabled={disabled}
      error={invalid}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
      slotProps={{
        htmlInput: { min: 1, max: MEDIA_KIT_METRICS_WINDOW_MAX_DAYS, step: 1 },
        input: {
          startAdornment: (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mr: 0.75, whiteSpace: "nowrap" }}
            >
              Día
            </Typography>
          ),
        },
      }}
      sx={{ width: 150 }}
    />
  );
};

export type MediaKitMetricsWindowFieldsProps = {
  value: MediaKitMetricsWindow;
  onChange: (next: MediaKitMetricsWindow) => void;
  disabled?: boolean;
  /** Hide the explanatory paragraph (compact list rows). */
  dense?: boolean;
};

export const MediaKitMetricsWindowFields: React.FC<
  MediaKitMetricsWindowFieldsProps
> = ({ value, onChange, disabled, dense }) => (
  <Box>
    <FormControlLabel
      control={
        <Switch
          size={dense ? "small" : "medium"}
          checked={value.enabled}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
        />
      }
      label={
        <Typography variant="body2" fontWeight={500}>
          Comparar por días desde publicación
        </Typography>
      }
    />
    {!dense && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Cada contenido muestra sus métricas al día N desde que se publicó, para
        comparar meses en igualdad de condiciones. Los contenidos que aún no
        llegan al día N muestran su último valor y se marcan como “en curso”.
      </Typography>
    )}
    {value.enabled && (
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
        <DayField
          label="Episodios"
          value={value.longFormDays}
          disabled={disabled}
          onCommit={(longFormDays) => onChange({ ...value, longFormDays })}
        />
        <DayField
          label="Shorts"
          value={value.shortsDays}
          disabled={disabled}
          onCommit={(shortsDays) => onChange({ ...value, shortsDays })}
        />
      </Stack>
    )}
  </Box>
);

export default MediaKitMetricsWindowFields;
